/**
 * Canonical customer label for every lookup, dropdown and search result.
 *
 * Format: `<Organization Name> | <Customer Code>`
 *
 *   ABC Manufacturing Pvt Ltd | CUS00001
 *
 * A customer picker that showed only `name` was ambiguous whenever two
 * customers shared one — the reported case had two rows both reading
 * "Sibnarayan". The customer code is what actually disambiguates them.
 *
 * The former three-segment form also appended a contact segment that fell back
 * to `name`, which for organisational customers repeated the organisation
 * almost verbatim (`CUS00001 | ABC Manufacturing Pvt Ltd | ABC Manufacturing`).
 * That duplicate is gone: the organisation leads, the code trails.
 *
 * Rules:
 *  - Organization first, Customer Code second.
 *  - Organization falls back to `name` when unset — for a private individual
 *    the personal name is the only identifying text, so it must not be dropped.
 *    "Individual" remains the last resort when both are blank.
 *  - Empty segments are dropped entirely, so no label ever renders a dangling
 *    or doubled separator.
 */

/** The minimum a customer-like object must expose to be labelled. */
export interface CustomerLabelSource {
  name: string;
  customerCode?: string | null;
  organizationName?: string | null;
  /** Populated by list endpoints; absent elsewhere, hence optional. */
  primaryContactName?: string | null;
}

export const CUSTOMER_LABEL_SEPARATOR = ' | ';

/** Shown when a customer has no organisation — i.e. a private individual. */
export const INDIVIDUAL_LABEL = 'Individual';

const clean = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * Builds the display label. Segments that would be empty are omitted rather
 * than rendered as a blank between separators.
 */
export function formatCustomerLabel(customer: CustomerLabelSource): string {
  const code = clean(customer.customerCode);
  // Organisation leads. A private individual has none, so the personal name
  // stands in — dropping it would leave the row identified only by its code.
  const organization = clean(customer.organizationName) || clean(customer.name) || INDIVIDUAL_LABEL;

  return [organization, code].filter((part) => part.length > 0).join(CUSTOMER_LABEL_SEPARATOR);
}

/**
 * Free-text haystack for client-side filtering, matching the fields the API
 * searches server-side: code, organisation, contact name, email, mobile.
 */
export function customerSearchText(
  customer: CustomerLabelSource & {
    email?: string | null;
    phone?: string | null;
    alternatePhone?: string | null;
  },
): string {
  return [
    customer.customerCode,
    customer.organizationName,
    customer.name,
    customer.primaryContactName,
    customer.email,
    customer.phone,
    customer.alternatePhone,
  ]
    .map(clean)
    .filter((part) => part.length > 0)
    .join(' ')
    .toLowerCase();
}

/** True when the customer matches a free-text query across all searchable fields. */
export function customerMatchesSearch(
  customer: Parameters<typeof customerSearchText>[0],
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return customerSearchText(customer).includes(q);
}
